import React, { FC, useCallback, useContext, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Grid, Typography } from '@mui/material'
import { makeStyles } from '@mui/styles'
import { Theme } from '@mui/material/styles'
import { FormProvider, useForm } from 'react-hook-form'
import { endOfDay, startOfDay } from 'date-fns'
import { OrderStatus, OrderType, ReadTradingHistoryRequestDto } from '@sparling/financial-common'
import { IDataItem, TableComponent } from '../../../../../components/Dashboard/Table'
import { getTradingHistoryRequest } from '../../../../../store/actions'
import { getTradingHistoryData, tradingHistoryIsLoading } from '../../../../../store/selectors'
import { AppStateContext } from '../../../../../components/Context'
import { data } from './TradingHistoryList'
import { RootState } from '../../../../../store/types'
import { DataPicker } from '../../../../../components/Dashboard/DatePicker'
import { InputSelect } from '../../../../../components/Dashboard/InputSelect'
import { ORDER_SIDE } from '../../../../../constants/orderSideList'
import { ORDER_STATUS } from '../../../../../constants/orderStatusList'
import { InputField } from '../../../../../components/Dashboard/InputField'
import { icons } from '../../../../../constants/icons'

const useStyles = makeStyles((theme: Theme) => ({
  wrap: {
    display: 'flex',
    width: '100%',
    height: '100%',
    flexDirection: 'column',
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },
  container: {
    display: 'flex',
    justifyContent: 'space-between',
    minHeight: 40,
    maxHeight: 40,
    paddingBottom: theme.spacing(2),
  },
  section: {
    justifyContent: 'space-between',
    padding: theme.spacing(1, 3, 1, 3),
    background: theme.palette.background.paper,
  },
  topWrap: {
    paddingTop: theme.spacing(2),
  },
}))

type Inputs = {
  startDate?: string
  endDate?: string
  type?: string
  status?: string
  search?: string
}

export interface TradingHistoryProps {
  count?: number
}

export const TradingHistory: FC<TradingHistoryProps> = ({ count = 10 }) => {
  const classes = useStyles()
  const { getUserIdSf } = useContext(AppStateContext)
  const idSf = getUserIdSf()
  const dispatch = useDispatch()
  const loading = useSelector(tradingHistoryIsLoading)
  const { items, meta } = useSelector(getTradingHistoryData)
  const authToken = useSelector((state: RootState) => state.app.authToken)

  const formMethods = useForm<Inputs>()

  const { watch, getValues } = formMethods

  const loadAccountsData = useCallback(
    (page = 1) => {
      if (authToken && idSf) dispatch(getTradingHistoryRequest({ idSf, page, count }))
    },
    [authToken, idSf, dispatch, count],
  )

  useEffect(() => {
    loadAccountsData()
  }, [loadAccountsData])

  const watchChange = useCallback(() => {
    if (!authToken || !idSf) return
    const values = getValues() as Pick<
      ReadTradingHistoryRequestDto,
      'startDate' | 'endDate' | 'type' | 'status' | 'search'
    >
    const { startDate, endDate, type, status, search } = values
    dispatch(
      getTradingHistoryRequest({
        idSf,
        startDate,
        endDate,
        type: type ? Object.values(OrderType).find((value) => value.replace(/\s/g, '_') === type) || type : undefined,
        status: status
          ? Object.values(OrderStatus).find((value) => value.replace(/\s/g, '_') === status) || status
          : undefined,
        search,
        page: 1,
        count,
      }),
    )
  }, [authToken, idSf, getValues, dispatch, count])

  useEffect(() => {
    const subscription = watch(watchChange)
    return () => subscription.unsubscribe()
  }, [watch, watchChange])

  return (
    <Grid item xs={12} className={classes.wrap}>
      <Grid item xs={12} className={classes.container}>
        <Typography variant={'h6'}>Trading History</Typography>
      </Grid>
      <Grid container item xs={12} className={classes.section}>
        <FormProvider {...formMethods}>
          <Grid container direction={'row'} className={classes.topWrap}>
            <Grid item>
              <DataPicker
                placeholder={'Start Date'}
                margin={'0 8px'}
                dataPickerName={'startDate'}
                required={false}
                maxDate={getValues().endDate}
                formatValue={startOfDay}
              />
            </Grid>
            <Grid item>
              <DataPicker
                placeholder={'End Date'}
                margin={'0 8px'}
                dataPickerName={'endDate'}
                required={false}
                minDate={getValues().startDate}
                formatValue={endOfDay}
              />
            </Grid>
            <Grid item>
              <InputSelect
                placeholderDisabled={false}
                inputName={'type'}
                placeholder="Side"
                width={160}
                enums={ORDER_SIDE}
                ml={8}
                mr={8}
              />
            </Grid>
            <Grid item>
              <InputSelect
                placeholderDisabled={false}
                inputName={'status'}
                placeholder="Status"
                width={160}
                enums={ORDER_STATUS}
                ml={8}
                mr={8}
              />
            </Grid>
            <Grid item>
              <InputField
                inputName="search"
                placeholder="Search"
                inputType="text"
                marginBottom={'0'}
                fontSize={16}
                ml={8}
                mr={8}
                borderColor={'rgba(255,255,255,0.6)'}
                prefix={icons.SEARCH}
              />
            </Grid>
          </Grid>
        </FormProvider>
        <TableComponent
          page={meta.currentPage}
          columns={data.columns}
          data={items as unknown as IDataItem[]}
          params={
            count < 10
              ? {
                  currentPage: 1,
                  itemsPerPage: count,
                  itemCount: 0,
                  totalItems: 0,
                  totalPages: 0,
                }
              : meta
          }
          loading={loading}
          loadData={loadAccountsData}
        />
      </Grid>
    </Grid>
  )
}
